import { loadSupersetTable } from '../charts/superset-bundle.js';
import { DRILLDOWN_EVENT, returnFocusAfterDrilldown } from '../charts/cell-actions.js';
import { ROW_ACTIONS, dropIndex, gridMountPayload, movedOrder, resolveCellAction, resolveRowAction } from '../charts/superset-table-grid.js';
import { focusInView } from './focus-in-view.js';

/**
 * The Alpine half of <x-charts.superset-table>: a scorecard grid card's table, drawn by BlinkTable
 * from the blink-superset bundle (documentation/context/blink-superset-bundle.md).
 *
 * Payloads arrive the way a chart host's contracts do, so a page forwards a grid exactly as it
 * forwards a chart: the server-rendered first paint, then `superset-chart-data` on window, keyed
 * by this host's id. The detail carries `table` (ScorecardGridSchema's payload) with
 * `lockedDrilldownRows`, or `status` ('loading' | 'failed' | 'empty') with `emptyMessage`, and may
 * rename the table with `title`. Retry dispatches `superset-chart-retry` {id}, as the chart host
 * does.
 *
 * Rows. A row's name opens its chart where its table says so (`opensChart`), and while the page
 * edits rows (tb_setEditing(), which a page drives from its own edit state, e.g.
 * `x-effect="tb_setEditing($wire.isArranging)"`) each row header also has a drag handle and a
 * remove button. Nothing about a row is changed here: each asks the page, in an event that bubbles
 * from this host's root carrying its `id` and the row's `rowKey`:
 *
 *  - ROW_OPEN_EVENT, the name pressed, with `findTrigger()`, which finds the name's button again
 *    for focus to go back to;
 *  - ROW_REMOVE_EVENT, the remove button, with the row's `metricId`;
 *  - ROW_MOVE_EVENT, a drop or an arrow key on the handle, with `toIndex`, where the row goes.
 *
 * The page's answer is the table drawn again, and focus stays with the control that held it
 * through any draw, found again by its row, column and action, so a moved row's handle keeps it.
 */

const DATA_EVENT = 'superset-chart-data';

/**
 * `{ids}`: a surface is done with these hosts, so what is held for them is dropped and a host that
 * mounts under one of those ids later starts from its first paint. A report's tabs send it when the
 * page changes tab (report-page.js): card ids are unique within the report, so a card's host mounts
 * again under the same id when its tab is opened again, and would otherwise replay whatever the
 * previous visit was sent.
 */
const FORGET_EVENT = 'superset-chart-forget';
const RETRY_EVENT = 'superset-chart-retry';

/** A row's name, remove button and handle asking the page: see the module's docblock. */
export const ROW_OPEN_EVENT = 'superset-table-row-open';
export const ROW_REMOVE_EVENT = 'superset-table-row-remove';
export const ROW_MOVE_EVENT = 'superset-table-row-move';

/** The latest detail per id, replayed to a host that starts after its event fired. */
const held = new Map();

/** The live host per id: one listener routes by id. */
const hosts = new Map();

let routing = false;

/** Numbers each mount's id prefix, so two grids on one page never share a header id. */
let mounts = 0;

/** A status replaces the table it announces, and a table replaces the status. */
function remember(detail) {
    const next = { ...(held.get(detail.id) ?? {}), ...detail };

    if ('status' in detail && ! ('table' in detail)) delete next.table;
    if ('table' in detail && ! ('status' in detail)) delete next.status;

    held.set(detail.id, next);
}

/**
 * Installed by the first host rather than at import, like the chart host's: a page that draws no
 * table should not listen for one. The chart host keeps its own router over the same event; each
 * answers only the ids it holds.
 */
function route() {
    if (routing) return;
    routing = true;

    window.addEventListener(DATA_EVENT, (event) => {
        const detail = event.detail;
        if (! detail || typeof detail.id !== 'string') return;

        remember(detail);
        hosts.get(detail.id)?.(detail);
    });

    // A card id is unique within its page only, and wire:navigate keeps this module alive.
    document.addEventListener('livewire:navigating', () => held.clear());

    window.addEventListener(FORGET_EVENT, (event) => {
        const ids = event.detail?.ids;
        if (Array.isArray(ids)) ids.forEach((id) => held.delete(id));
    });
}

function readJson(node) {
    return node ? JSON.parse(node.textContent || 'null') : null;
}

function fill(template, values) {
    return Object.entries(values).reduce((text, [token, value]) => text.split(token).join(value), String(template ?? ''));
}

function buttonFor(root, { rowKey, columnKey, action }) {
    const escape = (value) => (typeof CSS !== 'undefined' && CSS.escape ? CSS.escape(value) : String(value).replace(/["\\]/g, '\\$&'));

    return root?.querySelector(`button[data-action="${escape(action)}"][data-row-key="${escape(rowKey)}"][data-column-key="${escape(columnKey)}"]`) ?? null;
}

/** The table's button holding focus, by the keys buttonFor() finds it again with; null for anything else. */
function focusedControl(root) {
    const active = typeof document === 'undefined' ? null : document.activeElement;

    if (! active || active.tagName !== 'BUTTON' || ! root?.contains(active)) return null;

    const keys = ['action', 'row-key', 'column-key'].map((name) => active.getAttribute(`data-${name}`));

    return keys.every((key) => typeof key === 'string') ? { action: keys[0], rowKey: keys[1], columnKey: keys[2] } : null;
}

function sameControl(a, b) {
    return Boolean(a && b) && a.action === b.action && a.rowKey === b.rowKey && a.columnKey === b.columnKey;
}

/**
 * The port scrolled to its right end, the newest period: `instant` whatever scroll-behavior a
 * stylesheet gives it, and sideways only, so the reader's place down the rows is kept.
 */
function scrollToNewest(scroller) {
    scroller?.scrollTo({ left: scroller.scrollWidth, behavior: 'instant' });
}

export default function SupersetTable(config = {}) {
    const text = config.text ?? {};
    const idPrefix = `superset-table-${++mounts}-`;

    // Kept in the closure, not as reactive properties: Alpine deep-proxies what it holds, and the
    // mounted React tree must not be proxied.
    let table = null;
    let lockedDrilldownRows = Array.isArray(config.lockedDrilldownRows) ? config.lockedDrilldownRows : [];
    let token = 0;
    let bundle = null;
    let mountedOn = null;
    let receive = null;
    let lastSettled = null;
    let visible = ! config.lazy;
    let waiting = null;
    let viewObserver = null;
    let sizeObserver = null;
    let stopWatchingDrilldown = null;
    let revealFocused = null;
    // Whether the page edits rows (tb_setEditing()), the columns the table was last drawn with (a
    // new set opens scrolled to the newest), the order the host last asked the page for (a second
    // arrow key before the next draw counts from it), and the row being dragged.
    let editing = false;
    let drawnColumns = null;
    let askedOrder = null;
    let drag = null;
    let pressHandle = null;
    // Whether the grid shows its newest period: true until the reader scrolls away from it, and
    // again once they scroll back to it. While it holds, a width change keeps the newest in view.
    let atNewest = true;
    let followScroll = null;

    return {
        tb_id: String(config.id ?? ''),
        tb_title: String(config.title ?? ''),
        tb_labelledBy: config.labelledBy ? String(config.labelledBy) : null,
        tb_palette: config.palette ?? null,
        tb_text: {
            loading: text.loading ?? 'Loading…',
            loadFailed: text.loadFailed ?? 'This table could not be loaded.',
            retry: text.retry ?? 'Retry',
            noData: text.noData ?? 'No data for the selected period.',
            rowHeader: text.rowHeader ?? 'Metric',
            openDetails: text.openDetails ?? 'Open details',
            openSource: text.openSource ?? 'Open in its source, opens in a new tab',
            openSourceFor: text.openSourceFor ?? 'Open :row, :period in its source, opens in a new tab',
            recordMin: text.recordMin ?? 'The lowest in this row.',
            recordMax: text.recordMax ?? 'The highest in this row.',
            goalHeader: text.goalHeader ?? 'Goal',
            noGoal: text.noGoal ?? 'No goal',
            soFarProjected: text.soFarProjected ?? 'so far, projected',
            notProjected: text.notProjected ?? ':value, not projected',
            openRowChart: text.openRowChart ?? 'Opens its chart and records.',
            openRowChartOnly: text.openRowChartOnly ?? 'Opens its chart.',
            openRowTitle: text.openRowTitle ?? 'Open the chart and records for :metric',
            openRowChartTitle: text.openRowChartTitle ?? 'Open the chart for :metric',
            reorderRow: text.reorderRow ?? 'Reorder :metric, row :position of :total',
            removeRow: text.removeRow ?? 'Remove :metric from :grid',
            announceFailed: text.announceFailed ?? ':title could not be loaded.',
            announceEmpty: text.announceEmpty ?? ':title has no data for the selected period.',
            announceEmptyBecause: text.announceEmptyBecause ?? ':title: :value',
            announceLoaded: text.announceLoaded ?? ':title loaded.',
        },
        tb_state: 'loading',
        tb_drawn: false,
        tb_emptyMessage: '',
        tb_announcement: '',
        tb_scrolls: false,

        /** Prefixed to every id this host's table and its row hints render. */
        tb_idPrefix: idPrefix,

        /** Where a dragged row would land, as the drop line's top in px within the table's box; null hides it. */
        tb_dropTop: null,

        init() {
            route();
            receive = (detail) => this.tb_ingest(detail);
            hosts.set(this.tb_id, receive);

            if (typeof ResizeObserver !== 'undefined') {
                sizeObserver = new ResizeObserver(() => {
                    this.tb_syncScroll();
                    this.tb_keepNewest();
                });
                sizeObserver.observe(this.$refs.scroller);
                sizeObserver.observe(this.$refs.mount);
            }

            revealFocused = (event) => this.tb_revealFocused(event.target);
            this.$refs.scroller.addEventListener('focusin', revealFocused);
            pressHandle = (event) => this.tb_dragStart(event);
            this.$refs.mount.addEventListener('pointerdown', pressHandle);
            followScroll = () => {
                const scroller = this.$refs.scroller;

                atNewest = scroller.scrollWidth - scroller.clientWidth - scroller.scrollLeft <= 2;
            };
            this.$refs.scroller.addEventListener('scroll', followScroll, { passive: true });

            // An event that fired before this host existed is newer than the first paint.
            const replay = held.get(this.tb_id) ?? {};
            this.tb_ingest(replay.table || replay.status
                ? replay
                : { ...replay, id: this.tb_id, table: readJson(this.$root.querySelector('script[data-superset-table-initial]')) });
        },

        destroy() {
            if (hosts.get(this.tb_id) === receive) hosts.delete(this.tb_id);
            token += 1;
            this.tb_unmount();
            sizeObserver?.disconnect();
            sizeObserver = null;
            viewObserver?.disconnect();
            viewObserver = null;
            stopWatchingDrilldown?.();
            this.$refs.scroller?.removeEventListener('focusin', revealFocused);
            this.$refs.mount?.removeEventListener('pointerdown', pressHandle);
            this.$refs.scroller?.removeEventListener('scroll', followScroll);
            this.tb_dragEnd(false);
        },

        tb_ingest(detail) {
            if (detail.title) this.tb_title = String(detail.title);
            if (Array.isArray(detail.lockedDrilldownRows)) lockedDrilldownRows = detail.lockedDrilldownRows;

            if (detail.status === 'failed' || detail.status === 'loading' || detail.status === 'empty') {
                table = null;
                token += 1;
                this.tb_unmount();

                if (detail.status === 'loading') {
                    this.tb_state = 'loading';
                    return;
                }

                if (detail.status === 'empty') {
                    this.tb_emptyMessage = detail.emptyMessage || this.tb_text.noData;
                    this.tb_settle('empty', detail.emptyMessage);
                    return;
                }

                this.tb_settle('failed');
                return;
            }

            if (! detail.table) {
                // The viewer's locked rows or the table's name changed under a table already
                // drawn: its buttons and its name are drawn from them, so draw it again.
                if (table && ('lockedDrilldownRows' in detail || detail.title)) this.tb_draw();
                return;
            }

            table = detail.table;
            askedOrder = null;
            this.tb_draw();
        },

        /**
         * The page's edit state: while it edits rows, each row header gets a drag handle and a
         * remove button. The table held is drawn again in place, with no skeleton between.
         */
        tb_setEditing(on) {
            const next = on === true;

            if (next === editing) return;

            editing = next;
            this.tb_dragEnd(false);

            if (bundle && mountedOn && this.tb_state === 'table' && table) {
                this.tb_mount(token);
            } else if (table) {
                this.tb_draw();
            }
        },

        tb_draw() {
            const job = ++token;

            if (! visible) {
                this.tb_state = 'loading';
                waiting = job;
                this.tb_watchView();
                return;
            }

            // 'table' before the mount: the scroll port is display:none in every other state, and
            // the table measures its box when it mounts. The skeleton covers it until then.
            this.tb_state = 'table';
            this.tb_drawn = false;

            loadSupersetTable()
                .then((loaded) => {
                    bundle = loaded;
                    this.$nextTick(() => this.tb_mount(job));
                })
                .catch((error) => this.tb_fail(job, error));
        },

        tb_mount(job) {
            if (job !== token || ! table) return;

            const element = this.$refs.mount;
            // A new payload draws every cell anew, so the control holding focus is found again
            // afterwards by its row, column and action: a moved row's handle keeps focus.
            const focused = focusedControl(element);

            try {
                bundle.mountBlinkTable(element, gridMountPayload(table, {
                    palette: this.tb_palette,
                    text: this.tb_text,
                    lockedDrilldownRows,
                    onCellAction: (event) => this.tb_act(event),
                    editing,
                    gridTitle: this.tb_title,
                    rowHints: { records: `${idPrefix}row-open-hint`, chart: `${idPrefix}row-chart-hint` },
                    tableLabelledBy: this.tb_labelledBy ?? undefined,
                    tableLabel: this.tb_labelledBy ? undefined : this.tb_title,
                    idPrefix,
                    height: this.$refs.scroller.clientHeight || undefined,
                }));
                mountedOn = element;
            } catch (error) {
                this.tb_fail(job, error);
                return;
            }

            if (focused && ! sameControl(focusedControl(element), focused)) buttonFor(element, focused)?.focus();

            this.tb_drawn = true;
            this.tb_settle('table');
            this.tb_syncScroll();
            this.tb_openAtNewest();
        },

        /**
         * A fresh draw opens the grid scrolled to its newest period, the running one, as the
         * scorecard page opens, with the pinned row names still in view. A draw is fresh when no
         * table over these periods was up: the grid's first (a tab switch mounts a new host), one
         * after a status took the last table down (Apply filters, Reset and the filter chips put
         * every card back to loading first, an Interval change among them; so does Retry), or one
         * of other periods (another interval or dates by Back or Forward, a row reaching further
         * back). Drawn again over the same periods (edit mode, a row moved, one added or removed
         * that leaves the periods as they were), the grid keeps where the reader scrolled it, as
         * it does through a width change (tb_keepNewest()).
         *
         * Scrolled now, not on the next frame: a status hides the port, and when the table shows
         * it again the browser can put the port back at its left end and fire a scroll event for
         * it before a requestAnimationFrame callback runs (seen in Chromium when the new table
         * arrives a frame or more after the status; not reproduced on the report page itself).
         * That event would read as the reader scrolling away from the newest, and a scroll
         * waiting behind `atNewest` would never come. Scrolled now, the event finds the newest in
         * view.
         */
        tb_openAtNewest() {
            const columns = `${table?.interval}|${(table?.columns ?? []).map((column) => column.key).join(',')}`;

            if (columns === drawnColumns) return;

            drawnColumns = columns;
            atNewest = true;
            scrollToNewest(this.$refs.scroller);
            // And once more on the next frame, for a table that widens as it settles.
            this.tb_keepNewest();

            // A control in the table that holds focus is then brought back into view, queued
            // after that frame's scroll, so it is never left scrolled past.
            const active = typeof document === 'undefined' ? null : document.activeElement;
            if (active && mountedOn?.contains(active)) this.tb_revealFocused(active);
        },

        /**
         * A width change (the window, a panel opening beside the report, the card resized) moves
         * where the newest period sits, and a kept pixel offset can leave it out of view. So while
         * the reader has not scrolled away from it, the grid is kept scrolled to it; once they
         * have, where they scrolled is theirs.
         */
        tb_keepNewest() {
            if (! atNewest || ! mountedOn) return;

            requestAnimationFrame(() => {
                if (atNewest) scrollToNewest(this.$refs.scroller);
            });
        },

        tb_unmount() {
            this.tb_dragEnd(false);
            drawnColumns = null;

            if (mountedOn && bundle) {
                try {
                    bundle.unmountBlinkTable(mountedOn);
                } catch (error) {
                    console.debug('unmountBlinkTable failed during table cleanup:', error);
                }
            }
            mountedOn = null;
            this.tb_scrolls = false;
        },

        tb_fail(job, error) {
            if (job !== token) return;

            this.tb_unmount();
            console.error('Superset table failed to draw', { id: this.tb_id, error });
            this.tb_settle('failed');
        },

        tb_watchView() {
            if (viewObserver) return;

            // No IntersectionObserver means no way to tell, so draw rather than never.
            if (typeof IntersectionObserver === 'undefined') {
                visible = true;
                waiting = null;
                this.tb_draw();
                return;
            }

            viewObserver = new IntersectionObserver((entries) => {
                if (! entries.some((entry) => entry.isIntersecting)) return;

                visible = true;
                viewObserver.disconnect();
                viewObserver = null;

                if (waiting === token) this.tb_draw();
                waiting = null;
            }, { threshold: 0.1 });
            viewObserver.observe(this.$root);
        },

        /**
         * The scroll port is a tab stop, a region named by the card heading, only while the table
         * overflows it: a keyboard user can then scroll it with the arrow keys (2.1.1), and a table
         * that fits costs no extra stop. It keeps the stop while it holds focus, or focus would
         * fall to <body>.
         */
        tb_syncScroll() {
            requestAnimationFrame(() => {
                const scroller = this.$refs.scroller;
                if (! scroller) return;

                const overflows = mountedOn !== null
                    && (scroller.scrollWidth > scroller.clientWidth + 1 || scroller.scrollHeight > scroller.clientHeight + 1);

                this.tb_scrolls = overflows || document.activeElement === scroller;
            });
        },

        /**
         * The header row and the row headers are pinned inside the port (freezeEdges), and the
         * browser scrolls a focused cell only as far as the port's own edge, which can leave the
         * whole button under them (2.4.11). So once the browser has scrolled, a focused element in
         * the table's body is brought clear of both. Done here rather than with the port's
         * scroll-padding: Safari, given a left padding, stops scrolling a focused cell into view
         * at all.
         */
        tb_revealFocused(target) {
            const scroller = this.$refs.scroller;
            if (! mountedOn?.contains(target) || target.closest('thead')) return;

            // A row header is pinned sideways, so only its height can leave it under the header row.
            const isRowHeader = Boolean(target.closest('th'));

            requestAnimationFrame(() => {
                const head = mountedOn?.querySelector('thead');
                if (document.activeElement !== target || ! head) return;

                const box = target.getBoundingClientRect();
                const port = scroller.getBoundingClientRect();
                // The pinned columns' right edge: every header is pinned to the top, and only the
                // row headers' and the goals' to the left as well.
                const pinned = [...head.querySelectorAll('tr:first-child > th')]
                    .filter((header) => getComputedStyle(header).left !== 'auto')
                    .map((header) => header.getBoundingClientRect().right);
                const clearLeft = pinned.length > 0 ? Math.max(...pinned) : port.left;
                const clearTop = head.getBoundingClientRect().bottom;
                const viewRight = port.left + scroller.clientLeft + scroller.clientWidth;
                const viewBottom = port.top + scroller.clientTop + scroller.clientHeight;

                if (! isRowHeader && box.left < clearLeft) scroller.scrollLeft -= clearLeft - box.left;
                else if (! isRowHeader && box.right > viewRight) scroller.scrollLeft += Math.min(box.right - viewRight, box.left - clearLeft);

                if (box.top < clearTop) scroller.scrollTop -= clearTop - box.top;
                else if (box.bottom > viewBottom) scroller.scrollTop += Math.min(box.bottom - viewBottom, box.top - clearTop);
            });
        },

        /**
         * A cell's button was pressed. The table is asked again what the button opens, so an
         * action it no longer offers this viewer opens nothing.
         */
        tb_act(event) {
            if (Object.values(ROW_ACTIONS).includes(event.action)) {
                this.tb_rowAct(event);

                return;
            }

            const resolved = resolveCellAction(table, event, { lockedDrilldownRows });
            if (! resolved) return;

            if (resolved.kind === 'deeplink') {
                window.open(resolved.url, '_blank', 'noopener,noreferrer');
                return;
            }

            this.tb_returnFocusAfterDrilldown(event);
            window.Livewire?.dispatch(DRILLDOWN_EVENT, resolved.detail);
        },

        /**
         * A row header's control pressed: the page is asked, the table is asked again first what
         * the control offers (a locked row's name opens nothing, an edit control nothing outside
         * edit mode), and nothing here changes the table.
         */
        tb_rowAct(event) {
            const resolved = resolveRowAction(table, event, { lockedDrilldownRows, editing, order: askedOrder });
            if (! resolved) return;

            const rowKey = resolved.row.key;

            if (resolved.kind === 'open') {
                const findTrigger = () => buttonFor(this.$refs.mount, event);

                this.$dispatch(ROW_OPEN_EVENT, { id: this.tb_id, rowKey, label: resolved.row.label, findTrigger });

                return;
            }

            if (resolved.kind === 'remove') {
                this.$dispatch(ROW_REMOVE_EVENT, { id: this.tb_id, rowKey, metricId: resolved.row.metricId, label: resolved.row.label });

                return;
            }

            this.tb_askMove(rowKey, resolved.toIndex);
        },

        tb_askMove(rowKey, toIndex) {
            askedOrder = movedOrder(askedOrder ?? table.rows.map((row) => row.key), rowKey, toIndex);
            this.$dispatch(ROW_MOVE_EVENT, { id: this.tb_id, rowKey, toIndex });
        },

        /**
         * A row picked up by its handle, with the primary pointer: while it moves, a line shows
         * where the row would land (tb_dropTop), and letting go there asks the page to move it.
         * Escape, or the page leaving edit mode, drops it where it was.
         */
        tb_dragStart(event) {
            const handle = event.target?.closest?.('button[data-row-handle]');

            if (! handle || ! editing || ! mountedOn?.contains(handle) || (event.button ?? 0) !== 0) return;

            const rows = [...mountedOn.querySelectorAll('tbody > tr')];
            const row = handle.closest('tr');
            const from = rows.indexOf(row);
            const rowKey = handle.getAttribute('data-row-key');

            if (from === -1 || typeof rowKey !== 'string') return;

            event.preventDefault();
            handle.setPointerCapture?.(event.pointerId);

            const move = (moved) => this.tb_dragMove(moved);
            const drop = () => this.tb_dragEnd(true);
            const cancel = () => this.tb_dragEnd(false);
            const escape = (pressed) => {
                if (pressed.key !== 'Escape') return;

                pressed.preventDefault();
                pressed.stopPropagation();
                this.tb_dragEnd(false);
            };

            handle.addEventListener('pointermove', move);
            handle.addEventListener('pointerup', drop);
            handle.addEventListener('pointercancel', cancel);
            window.addEventListener('keydown', escape, true);

            drag = {
                rowKey,
                from,
                to: from,
                row,
                rows,
                release: () => {
                    handle.removeEventListener('pointermove', move);
                    handle.removeEventListener('pointerup', drop);
                    handle.removeEventListener('pointercancel', cancel);
                    window.removeEventListener('keydown', escape, true);
                    if (handle.hasPointerCapture?.(event.pointerId)) handle.releasePointerCapture(event.pointerId);
                },
            };
            row.style.opacity = '0.45';
        },

        tb_dragMove(event) {
            if (! drag) return;

            const middles = drag.rows.map((row) => {
                const box = row.getBoundingClientRect();

                return box.top + box.height / 2;
            });
            const to = dropIndex(middles, drag.from, event.clientY);

            drag.to = to;

            if (to === drag.from) {
                this.tb_dropTop = null;

                return;
            }

            // The line goes above the row it lands before, or under the row it lands after, kept
            // inside the scroll port when that row is scrolled out of it.
            const target = drag.rows[to].getBoundingClientRect();
            const frame = this.$refs.scroller.getBoundingClientRect();
            const top = (to < drag.from ? target.top : target.bottom) - frame.top;

            this.tb_dropTop = Math.round(Math.max(0, Math.min(frame.height, top)));
        },

        tb_dragEnd(isDrop) {
            if (! drag) return;

            const { rowKey, from, to, row, release } = drag;

            drag = null;
            release();
            row.style.opacity = '';
            this.tb_dropTop = null;

            if (isDrop && to !== from) this.tb_askMove(rowKey, to);
        },

        /**
         * Focus goes back to the cell's button when the drilldown closes, if it landed nowhere
         * (returnFocusAfterDrilldown(), which says why x-trap alone does not). The button is found
         * again by its keys in case the table was drawn again meanwhile.
         */
        tb_returnFocusAfterDrilldown(target) {
            stopWatchingDrilldown?.();
            stopWatchingDrilldown = returnFocusAfterDrilldown(() => buttonFor(this.$refs.mount, target));
        },

        /**
         * Announces a change of state, never the first settle: every card on a report settles on
         * load, and the page says once that it has loaded. New data for a table already drawn is
         * silent too; the table itself has changed.
         */
        tb_settle(next, reason = null) {
            this.tb_state = next;

            const previous = lastSettled;
            lastSettled = next;
            if (previous === null || previous === next) return;

            if (next === 'empty' && reason) {
                this.tb_announce(fill(this.tb_text.announceEmptyBecause, { ':title': this.tb_title, ':value': reason }));
                return;
            }

            const key = { failed: 'announceFailed', empty: 'announceEmpty', table: 'announceLoaded' }[next];
            this.tb_announce(fill(this.tb_text[key], { ':title': this.tb_title }));
        },

        /** Cleared first and set on the next frame, so the same sentence twice is spoken twice. */
        tb_announce(message) {
            this.tb_announcement = '';
            requestAnimationFrame(() => {
                this.tb_announcement = message;
            });
        },

        /**
         * Pressing Retry hides the button being pressed, so focus is parked first on the card's
         * heading (data-card-heading in the nearest data-card), or on the host's own frame, clear
         * of a pinned header (focusInView()). A table that failed to draw here is drawn again from
         * the one held; otherwise the page is asked for another, as the chart host asks.
         */
        tb_retry() {
            const heading = this.$root.closest('[data-card]')?.querySelector('[data-card-heading]');
            focusInView(heading ?? this.$refs.frame);

            if (table) {
                this.tb_draw();
                return;
            }

            this.tb_state = 'loading';
            this.$dispatch(RETRY_EVENT, { id: this.tb_id });
        },
    };
}
